import { useEffect, useRef, useState } from 'react';

interface UseExitIntentOptions {
  enabled?: boolean;
  sensitivity?: number;
  delay?: number;
  onExitIntent?: () => void;
}

export const useExitIntent = (options: UseExitIntentOptions = {}) => {
  const {
    enabled = true,
    sensitivity = 20,
    delay = 0,
    onExitIntent
  } = options;

  const [showExitIntent, setShowExitIntent] = useState(false);
  const hasTriggered = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!enabled || hasTriggered.current) {
      return;
    }

    const handleMouseLeave = (e: MouseEvent) => {
      // Only trigger if mouse is leaving from the top of the page
      if (e.clientY <= sensitivity && !hasTriggered.current) {
        hasTriggered.current = true;
        
        if (delay > 0) {
          timeoutRef.current = setTimeout(() => {
            setShowExitIntent(true);
            onExitIntent?.();
          }, delay);
        } else {
          setShowExitIntent(true);
          onExitIntent?.();
        }
      }
    };

    document.addEventListener('mouseout', handleMouseLeave);

    return () => {
      document.removeEventListener('mouseout', handleMouseLeave);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [enabled, sensitivity, delay, onExitIntent]);

  const resetExitIntent = () => {
    hasTriggered.current = false;
    setShowExitIntent(false);
  };

  const dismissExitIntent = () => {
    setShowExitIntent(false);
  };

  return {
    showExitIntent,
    resetExitIntent,
    dismissExitIntent
  };
};
